import type { Composer } from "vue-i18n";
import { defineStore } from "pinia";
import { DateTime } from "luxon";
import i18n from "@/Plugins/i18n";
import { LANG_STORAGE_KEY } from "@/Plugins/i18n";

import type { User, UserNextStep, UserProgress } from "@/Modules/Models/Domain/User";
import type { UserState } from "./Models/State";
import type { UserUiUpdatePasswordQuery, UserUiUpdateQuery } from "@/Modules/User/Models/Ui/User";
import type { Notification } from "@/Modules/App/Models/Domain/Notification";
import { UserStatus } from "@/Common/Models/Domain/UserStatus";
import { Language } from "@/Common/Models/Domain/Language";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { LocalStorageNamespace } from "@/Modules/App/Models/Localstorage/LocalStorageNamespace";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { Role } from "@/Common/Models/Domain/Role";
import { Country } from "@/Common/Models/Domain/Country";
import { LocalStorageKey } from "@/Modules/App/Models/Localstorage/LocalStorageKey";

import { useAppStore } from "@/Modules/App/Store";
import useAuthStore from "@/Modules/Auth/Store";

import updateUserAH from "./ActionHandlers/UpdateUser";
import updateUserPasswordAH from "./ActionHandlers/UpdateUserPassword";
import deleteAccountAH from "./ActionHandlers/DeleteAccount";
import setUserAdditionalDataAH from "./ActionHandlers/SetUserAdditionalData";
import getUserDataAH from "./ActionHandlers/GetUserData";
import setPromoCodeAH from "./ActionHandlers/SetPromoCode";
import showTherapistAH from "./ActionHandlers/ShowTherapist";
import setLastTherapistAH from "./ActionHandlers/SetLastTherapist";
import getB2bDocsAH from "./ActionHandlers/GetB2bDocs";
import setB2bDocsAH from "./ActionHandlers/SetB2bDocs";
import getNextStepsAH from "./ActionHandlers/GetNextSteps";
import getProgressActionHandler from "./ActionHandlers/GetProgress";

import { LocalStorageService } from "@/Services/LocalStorageService";

import { idGAEvent } from "@/Modules/App/TrackersAndCookies/GoogleGTM";
import { copyToClipboard } from "@/Utils/CopyToClipboard";
import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { navigatorLang } from "@/Utils/Language";
import { setLocaleApp } from "@/Utils/Date";

const userState: UserState = {
  user: null,
  isLoading: false,
  notification: null,
  emailExist: false,
  lastTherapist: null,
  userB2bDocs: [],
  progress: null,
};

export const useUserStore = defineStore(StateNamespace.USER, {
  state: () => ({ ...userState }),
  getters: {
    getUserIsTherapist(state): boolean {
      return state.user && state.user.role ? state.user.role === Role.THERAPIST : false;
    },

    getUserIsPatient(state): boolean {
      return state.user && state.user.role ? state.user.role === Role.CUSTOMER : false;
    },

    getUserInTransferPending(state): boolean {
      if (this.getUserIsTherapist) return false;

      return hasValue(state.user) && state.user.status === UserStatus.TRANSFER_PENDING;
    },

    getUserIsInactive(state): boolean {
      if (this.getUserIsTherapist) return false;

      return state.user?.status === UserStatus.INACTIVE;
    },

    getUserIsActive(state): boolean {
      if (this.getUserIsTherapist) return false;

      return state.user?.status === UserStatus.ACTIVE;
    },

    getUserIsUnassigned(state): boolean {
      if (this.getUserIsTherapist) return false;

      return state.user?.status === UserStatus.UNASSIGNED;
    },

    getUserIsB2bChangeEmail(state): boolean {
      return state.user ? state.user.b2bChangeEmail : false;
    },

    getUserHasB2bConditions(state): boolean {
      return hasValue(state.userB2bDocs) ? state.userB2bDocs.length > 0 : false;
    },

    isUserNotificationsActive(state: UserState): boolean {
      return hasValue(state.user) && state.user.isNotificationActive;
    },

    // if it has no value for 'last_subscription' means that the user was register but not end the onboarding process
    // Just if it's SSO Login, needs to go to /chat
    getUserInOnBoarding(state): boolean {
      if (this.getUserIsTherapist || !hasValue(state.user)) return false;

      return (
        (!hasValue(state.user?.lastSubscription) && this.getUserIsUnassigned) ||
        (state.user.status === UserStatus.ASSIGNED && state.user.isCustomerSSO) ||
        this.getUserIsInactive ||
        this.isReassignPatient
      );
    },

    // User than had therapy with us and come back.
    isUserComeback(state): boolean {
      if (this.getUserIsTherapist) return false;

      return hasValue(state.user) ? state.user.isCustomerComeback : false;
    },

    getUserInTherapistShow(state): boolean {
      if (this.getUserIsTherapist) return false;

      return this.getUserIsActive && hasValue(state.user) && state.user.therapistShow;
    },

    getLastTherapist(): User | undefined {
      if (this.lastTherapist) return this.lastTherapist;
    },

    // to configure the hubspotchat we need to set a query param to charge the chat for  patient or therapist.
    // talk to hubspot admin to change this. Look in chatflows / target.
    getQueryParamsHelpHubspot(state): { profile: string } {
      let role = state.user?.role || "";
      const language = state.user?.language || "";

      if (role === Role.CUSTOMER) {
        // in the url, it needs to appear patient instead customer.
        role = "patient";
      }

      return { profile: `${role}-${language}` };
    },

    isSSOPatient(state): boolean {
      if (this.getUserIsTherapist) return false;

      return hasValue(state.user) ? state.user.isCustomerSSO : false;
    },

    isReassignPatient(state): boolean {
      if (this.getUserIsTherapist) return false;

      return hasValue(state.user) ? state.user.isCustomerReassign : false;
    },

    getUserIsMexican(): boolean {
      return this.getCountry === Country.MX;
    },

    getCountry(state): Country | undefined {
      if (!hasValue(state.user)) return undefined;

      if (isValidString(state.user.country)) {
        return state.user.country;
      }

      return state.user.language as unknown as Country;
    },

    userHasTimeZone(state): boolean {
      return isValidString(state.user?.timeZone);
    },

    timezone(): string {
      const userTimeZone = this.user?.timeZone;

      if (isValidString(userTimeZone)) {
        return userTimeZone;
      }

      return DateTime.now().zoneName;
    },
  },

  actions: {
    async setUser(force = false) {
      if (!hasValue(this.user) || force) {
        const user = await getUserDataAH();

        if (hasValue(user)) {
          this.updateLocale(user.language);
          this.user = user;

          if (this.user.role === Role.CUSTOMER) {
            idGAEvent(user.id);

            await this.setUserAdditionalData();
          }

          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    },

    clearUser() {
      this.user = null;
    },

    async setLastTherapist() {
      await setLastTherapistAH();
    },

    updateLocale(language: Language) {
      (i18n.global as unknown as Composer).locale.value = language;
      setLocaleApp(language);
    },

    updateUserLanguage(lang: Language) {
      if (this.user) {
        this.user = { ...this.user, language: lang };
      }
    },

    async updateLanguage(language?: string) {
      const localStorageService: LocalStorageService = new LocalStorageService(
        LocalStorageNamespace.ON_BOARDING
      );
      const userStore = useUserStore();
      let lang = isValidString(language) ? (language as Language) : navigatorLang;

      if (!lang && hasValue(userStore.user?.language)) {
        lang = userStore.user.language;
      }

      localStorageService.set(LANG_STORAGE_KEY, lang);

      switch (lang) {
        case Language.SPANISH:
          userStore.updateLocale(Language.SPANISH);
          userStore.updateUserLanguage(Language.SPANISH);
          break;

        case Language.ITALIAN:
          userStore.updateLocale(Language.ITALIAN);
          userStore.updateUserLanguage(Language.ITALIAN);
          break;

        case Language.PORTUGUESE:
          userStore.updateLocale(Language.PORTUGUESE);
          userStore.updateUserLanguage(Language.PORTUGUESE);
          break;

        case Language.ENGLISH:
        default:
          userStore.updateLocale(Language.ENGLISH);
          userStore.updateUserLanguage(Language.ENGLISH);
      }
    },

    async setUserAdditionalData() {
      await setUserAdditionalDataAH();
    },

    async setPromoCode(promoCode: string): Promise<Notification | null> {
      if (this.user) {
        return setPromoCodeAH(this.user.id, promoCode);
      }

      return null;
    },

    async updateUser(userUiUpdateQuery: UserUiUpdateQuery) {
      await updateUserAH(userUiUpdateQuery);
    },

    clearNotification() {
      this.notification = null;
    },

    async updatePassword(userUiUpdatePasswordQuery: UserUiUpdatePasswordQuery) {
      await updateUserPasswordAH(userUiUpdatePasswordQuery);
    },

    async deleteAccount() {
      const userStore = useUserStore();
      const authStore = useAuthStore();

      if (userStore.user) {
        const uuid = userStore.user.id || "";

        await deleteAccountAH(uuid);
      }

      authStore.logout();
    },

    async updateNotificationUser(value: boolean, canNotify = true) {
      // this configuration is old, we need to leave like this:
      const appStore = useAppStore();

      await this.updateUser({ isNotificationActive: value });

      if (this.notification?.type === NotificationType.SUCCESS && canNotify) {
        appStore.showSnackbar({
          message: "settings.notification.update_success",
          type: NotificationType.SUCCESS,
        });
      }
    },

    copyReferralCode() {
      if (this.user?.referralCode) {
        const appStore = useAppStore();

        copyToClipboard(this.user?.referralCode);

        appStore.showSnackbar({
          message: "account.referral_code.notifivation_copy_message",
          type: NotificationType.INFO,
        });
      }
    },

    async showTherapist(): Promise<void> {
      if (hasValue(this.user)) this.user.therapistShow = false;

      await showTherapistAH();
    },

    async getB2bDocs(): Promise<void> {
      await getB2bDocsAH();
    },

    async setB2bDocs(): Promise<void> {
      await setB2bDocsAH();
    },

    saveTimeZoneDataInLocalStorage(timeZone: string): void {
      const configLocalStorageService: LocalStorageService = new LocalStorageService(
        LocalStorageNamespace.CONFIG
      );
      const LOCAL_STORAGE_TIME_ZONE_KEY: string = LocalStorageKey.TIME_ZONE;
      const LOCAL_STORAGE_TIME_ZONE_DATE_KEY: string = LocalStorageKey.TIME_ZONE_DATE;

      configLocalStorageService.set(LOCAL_STORAGE_TIME_ZONE_KEY, timeZone);
      configLocalStorageService.set(LOCAL_STORAGE_TIME_ZONE_DATE_KEY, Date.now().toString());
    },

    fetchNextSteps(): Promise<UserNextStep[]> {
      return getNextStepsAH();
    },

    fetchProgress(): Promise<UserProgress | null> {
      return getProgressActionHandler();
    },
  },
});

export type UserStore = ReturnType<typeof useUserStore>;

export default useUserStore;
